import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/lib/store";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, TrendingUp, Users, DollarSign, MousePointer } from "lucide-react";

export default function Partners() {
  const { user } = useApp();
  const isPartner = user?.role === "partner" || ["admin", "super_admin"].includes(user?.role);
  const [form, setForm] = useState({ name: "", email: "", company: "", website: "", social: "", audience: "", methods: "", notes: "" });
  const [dash, setDash] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isPartner) api.get("/partners/dashboard").then(({ data }) => setDash(data)).catch(() => {});
  }, [isPartner]);

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try { await api.post("/partners/apply", form); toast.success("Application submitted"); }
    catch { toast.error("Could not submit"); }
    setSubmitting(false);
  };

  const stats = dash ? [
    ["Clicks", dash.clicks, MousePointer], ["Conversions", dash.conversions, Users],
    ["Revenue", `$${dash.revenue.toFixed(2)}`, TrendingUp], ["Pending", `$${dash.commission_pending.toFixed(2)}`, DollarSign],
    ["Approved", `$${dash.commission_approved.toFixed(2)}`, DollarSign], ["Paid", `$${dash.commission_paid.toFixed(2)}`, DollarSign],
  ] : [];

  return (
    <div className="px-4 sm:px-8 lg:px-12 pt-28 pb-20 max-w-7xl mx-auto">
      <div className="mono-label mb-3">Referral platform</div>
      <h1 className="font-serif text-5xl sm:text-6xl text-white mb-4">CG Labz Partner Program</h1>
      <p className="text-slate-400 text-sm max-w-xl mb-12">Affiliates, research partners, content partners, ambassadors. Transparent commissions, real-time dashboard.</p>

      {!isPartner ? (
        <div className="glass rounded-3xl p-8 max-w-2xl">
          <div className="mono-label mb-4">Apply</div>
          <form onSubmit={submit} className="space-y-4">
            {[["name","Name *"],["email","Email *"],["company","Company"],["website","Website"],["social","Social profiles"],["audience","Audience size"],["methods","Promotion methods"]].map(([k, ph]) => (
              <Input key={k} required={ph.includes("*")} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph}
                data-testid={`partner-input-${k}`} className="bg-black/40 border-white/10" />
            ))}
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes"
              className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm min-h-[80px]" />
            <Button type="submit" disabled={submitting} data-testid="partner-apply-btn" className="w-full btn-primary rounded-xl py-3 text-sm">
              {submitting ? "Submitting…" : "Apply to Partner Program"}
            </Button>
          </form>
        </div>
      ) : dash ? (
        <div>
          <div className="glass rounded-2xl p-6 mb-6">
            <div className="mono-label mb-3">Your referral link</div>
            <div className="flex flex-wrap items-center gap-3">
              <code className="flex-1 min-w-0 bg-black/40 border border-cyan-400/20 rounded-xl px-4 py-3 text-sm font-mono text-cyan-300 truncate" data-testid="partner-link">{dash.link}</code>
              <Button onClick={() => { navigator.clipboard.writeText(dash.link); toast.success("Copied"); }} data-testid="partner-copy-link" className="btn-primary rounded-xl"><Copy size={14} /></Button>
            </div>
            <div className="mt-3 text-[11px] font-mono text-slate-500">Code: <span className="text-cyan-300">{dash.code}</span> · Track with ?ref= & utm_campaign=</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            {stats.map(([l, v, I]) => (
              <div key={l} className="glass rounded-2xl p-4">
                <I className="text-cyan-300 mb-2" size={16} />
                <div className="font-mono text-xl text-white">{v}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">{l}</div>
              </div>
            ))}
          </div>
          <div className="glass rounded-2xl p-6">
            <div className="mono-label mb-4">Recent commissions</div>
            <div className="space-y-2">
              {dash.recent.map((c) => (
                <div key={c.id} className="flex items-center justify-between border-b border-white/5 pb-2 text-sm font-mono" data-testid={`commission-${c.id}`}>
                  <span className="text-slate-400">{c.order_id}</span>
                  <span className="text-white">${c.order_total.toFixed(2)}</span>
                  <span className="text-cyan-300">${c.amount.toFixed(2)}</span>
                  <span className={`text-[10px] uppercase tracking-widest ${c.status === "paid" ? "text-emerald-400" : c.status === "approved" ? "text-cyan-300" : "text-amber-400"}`}>{c.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
